import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { cloneDeep, isEmpty } from 'lodash';
import { IAmendmentPageState, IInsuredData } from '../..';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import ProposalPolicyPersonDrawer from '../../../proposal/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import { removeObjectAtIndex } from '@utils/helper-utils';
import { validatePolicyInsuredField } from './validation';
import { extractPersonsAddressesFixed, extractPersonsFromQuery } from './utils';
import {
  fetchedPersonAddressList,
  fetchedPersonsWithPagination,
} from './queries';
import { IAmendmentGeneralData } from '../../line-amendment';
import { IAbstractRecord } from '@models';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { useResetKey } from '@hooks/useResetKey';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';

interface IInsuredWidgetProps {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled: boolean;
  maxNumber: number;
}

interface IInsuredLovs {
  id: Record<number, Record<string, string>>;
  insuredAddresses: Record<number, Record<string, string>>;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr',
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  fieldRowWithOrder: {
    gridTemplateColumns: '0.5fr 2fr 2fr',
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
    right: '0',
    margin: '0',
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 !important',
  },
  label: {
    color: MAIN_THEME.color.black,
    margin: '9px 0px 0!important',
    padding: '0px!important',
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
  labelCurrency: {
    color: MAIN_THEME.color.black,
    margin: '0px 0 -2px 0 !important',
    padding: '0px!important',
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
  paddingBottom22: {
    paddingBottom: '22px',
  },
  paddingBottom24: {
    paddingBottom: '25px',
  },
}));

const InsuredWidget: React.FC<IInsuredWidgetProps> = ({
  generalData,
  pageState,
  onPageStateUpdate,
  isDisabled,
  maxNumber,
}) => {
  const { id: currentAmendmentID } = useParams<{ id: string }>();
  const { classes } = useStyles();

  const isInsuredFieldVisible = [
    '5',
    '9',
    '10',
    '13',
    '21',
    '35',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
  ].includes(generalData.line.externalCode);

  const [loaded, setLoaded] = useState<boolean>(false);

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState<number>(-1);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState<number>(-1);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [autocompleteKey, resetAutocomplete] = useResetKey('insured');
  const [lists, setLists] = useState<IInsuredLovs>({
    id: {},
    insuredAddresses: {},
  });

  const insuredValues = pageState.values.policyPersons.insured;
  const insuredErrors = pageState.errors.policyPersons.insured;
  const insuredTouched = pageState.touched.policyPersons.insured;

  const [fetchedPersonsLazy] = useLazyQuery(fetchedPersonsWithPagination);
  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList);

  const handleOpenDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(rowIndex);
  };

  const handleOpenAddressDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewAddressDrawerOpen(rowIndex);
  };

  const handleInsuredAddresses = async (personId: string) => {
    const result = await getPersonAddressListLazy({
      variables: { selectedPersonID: personId },
      errorPolicy: 'all',
    });
    return extractPersonsAddressesFixed(result.data);
  };

  const initialize = () => {
    const newList: Record<number, Record<string, string>> = {};
    insuredValues.forEach((insured, index) => {
      if (!newList[index]) {
        newList[index] = {};
      }
      newList[index][insured.address] = insured.addressDetails;
    });
    setLists((prevLists) => ({
      ...prevLists,
      insuredAddresses: newList,
    }));
    setLoaded(true);
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldUpdate = async (
    fieldName: keyof IInsuredData,
    value: unknown,
    rowIndex: number,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newPageState = cloneDeep(pageState);

    if (fieldName === 'id') {
      newPageState.values.policyPersons.insured[rowIndex][fieldName] =
        value as string;
      newPageState.errors.policyPersons.insured[rowIndex][fieldName] =
        validatePolicyInsuredField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.insured[rowIndex][fieldName] = true;
      }

      if (!isEmpty(value)) {
        const searchAddressResult = await handleInsuredAddresses(
          value as string
        );

        const newLists = { ...updatedList };
        const insuredAddresses = searchAddressResult;
        const extractedAddresses: IAbstractRecord = {};
        if (insuredAddresses) {
          Object.keys(insuredAddresses).forEach((key) => {
            extractedAddresses[key] = insuredAddresses[key].address;
            if (insuredAddresses[key].isPrimary) {
              newPageState.values.policyPersons.insured[rowIndex].address = key;
              newPageState.values.policyPersons.insured[
                rowIndex
              ].addressDetails = insuredAddresses[key].address;
            }
          });
          newPageState.errors.policyPersons.insured[rowIndex].address = '';
          newLists['insuredAddresses'][rowIndex] = extractedAddresses;
          setLists(newLists);
        }
      }
    } else {
      (newPageState.values.policyPersons.insured[rowIndex] as any)[fieldName] =
        value;
      newPageState.errors.policyPersons.insured[rowIndex][fieldName] =
        validatePolicyInsuredField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.insured[rowIndex][fieldName] = true;
      }
    }

    onPageStateUpdate(newPageState);
  };

  const onPersonAddition = async (person: string, personFullName?: string) => {
    const rowIndex = newPersonDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.insured[rowIndex]['id'] = person;
    newPageState.errors.policyPersons.insured[rowIndex]['id'] = '';
    newPageState.touched.policyPersons.insured[rowIndex]['id'] = true;

    newLists.id[rowIndex] = {};
    newLists.id[rowIndex][person] = personFullName;

    const searchAddressResult = await handleInsuredAddresses(person);

    const insuredAddresses = searchAddressResult;
    const extractedAddresses: IAbstractRecord = {};
    if (insuredAddresses) {
      Object.keys(insuredAddresses).forEach((key) => {
        extractedAddresses[key] = insuredAddresses[key].address;
        if (insuredAddresses[key].isPrimary) {
          newPageState.values.policyPersons.insured[rowIndex].address = key;
          newPageState.values.policyPersons.insured[rowIndex].addressDetails =
            insuredAddresses[key].address;
        }
      });
      newPageState.errors.policyPersons.insured[rowIndex].address = '';
      newLists['insuredAddresses'][rowIndex] = extractedAddresses;
    }

    if (Object.keys(newLists['insuredAddresses'][rowIndex]).length === 1) {
      newPageState.values.policyPersons.insured[rowIndex]['address'] =
        Object.keys(newLists['insuredAddresses'][rowIndex])[0];
    }

    resetAutocomplete();
    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const onAddressAddition = async (
    address: string,
    addressFullName?: string
  ) => {
    const rowIndex = newAddressDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.insured[rowIndex]['address'] = address;
    newPageState.errors.policyPersons.insured[rowIndex]['address'] = '';
    newPageState.touched.policyPersons.insured[rowIndex]['address'] = true;

    newLists.insuredAddresses[rowIndex] = {};
    newLists.insuredAddresses[rowIndex][address] = addressFullName;

    newPageState.values.policyPersons.insured[rowIndex].address = address;
    newPageState.values.policyPersons.insured[rowIndex].addressDetails =
      addressFullName;

    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const renderInsuredRow = (row: IInsuredData) => {
    const rowIndex = insuredValues.indexOf(row);

    return (
      <div
        className={
          maxNumber === 1 && insuredValues.length === 1
            ? undefined
            : classes.container
        }
        key={rowIndex}
      >
        <div className={classes.clearBtn}>
          {!isDisabled && insuredValues.length > 1 && (
            <button
              className={classes.removeBtn}
              onClick={() => onInsuredRowDelete(rowIndex)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>
        <div
          className={clsx(classes.fieldRow, {
            [classes.fieldRowWithOrder]: maxNumber > 1,
          })}
        >
          {maxNumber > 1 && (
            <NumberInputFormField
              key="order"
              name="order"
              title="Order*"
              placeholder="Order"
              value={insuredValues[rowIndex].order}
              error={
                insuredTouched[rowIndex].order
                  ? insuredErrors[rowIndex].order
                  : ''
              }
              onChange={(e) => onFieldUpdate('order', e.target.value, rowIndex)}
              disabled={isDisabled}
              material
            />
          )}
          <WithLinkFormField
            linkText={isDisabled ? '' : 'Add Person'}
            addSpace={!isDisabled && !isInsuredFieldVisible && maxNumber <= 1}
            onLinkClick={() => {
              handleOpenDrawer('insured', rowIndex);
            }}
          >
            <AutocompleteFormField
              key={`${autocompleteKey}${rowIndex}`}
              name="id"
              title="Insured"
              placeholder="Insured"
              value={insuredValues[rowIndex].id}
              error={
                insuredTouched[rowIndex]?.id ? insuredErrors[rowIndex].id : ''
              }
              disabled={isDisabled}
              material
              onChange={(v) => {
                onFieldUpdate('id', v, rowIndex);
              }}
              fetchOptions={function (
                value,
                pagination,
                hasDefaultValue
              ): Promise<{
                options: Record<string, string>;
                totalCount: number;
              }> {
                const defaultValue = hasDefaultValue ? value : null;
                return fetchedPersonsLazy({
                  variables: {
                    searchKeyword: hasDefaultValue ? null : value,
                    pagination,
                    selectedPersonID: defaultValue,
                  },
                }).then((result) => {
                  const options = extractPersonsFromQuery(
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.items
                  );
                  return {
                    options,
                    totalCount:
                      result.data?.Production?.queries
                        ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                  };
                });
              }}
            />
          </WithLinkFormField>
          {!isInsuredFieldVisible && (
            <WithLinkFormField
              linkText={isDisabled ? '' : 'Add Address'}
              addSpace={!isDisabled && !isInsuredFieldVisible && maxNumber <= 1}
              onLinkClick={() => {
                handleOpenAddressDrawer('insured', rowIndex);
              }}
            >
              <SelectFormField
                key="address"
                name="address"
                title="Insured Address"
                placeholder="Insured Address"
                value={insuredValues[rowIndex].address}
                disabled={isDisabled}
                material
                error={
                  insuredTouched[rowIndex]?.address
                    ? insuredErrors[rowIndex].address
                    : ''
                }
                onChange={(v) => onFieldUpdate('address', v, rowIndex)}
                selectOptions={lists.insuredAddresses[rowIndex]}
              />
            </WithLinkFormField>
          )}
          {isInsuredFieldVisible && (
            <NumberInputFormField
              key="limitOfCover"
              name="limitOfCover"
              title="Limit of Cover"
              placeholder="Enter Limit of Cover"
              value={insuredValues[rowIndex].limitOfCover || ''}
              error={
                insuredTouched[rowIndex]?.limitOfCover
                  ? insuredErrors[rowIndex]?.limitOfCover
                  : ''
              }
              onChange={(v) =>
                onFieldUpdate('limitOfCover', v.target.value, rowIndex)
              }
              disabled={isDisabled}
              material
            />
          )}
        </div>
      </div>
    );
  };

  const onInsuredRowAdd = () => {
    if (!isDisabled && insuredValues.length < maxNumber) {
      const newPageState = cloneDeep(pageState);
      const newRowIndex = newPageState.values.policyPersons.insured.length;
      newPageState.values.policyPersons.insured.push({
        id: '',
        order: `${newRowIndex + 1}`,
        address: '',
      });

      newPageState.errors.policyPersons.insured.push({
        id: '',
        order: '',
        address: '',
      });

      newPageState.touched.policyPersons.insured.push({
        id: false,
        order: false,
        address: false,
      });

      newPageState.values.policyPersons.insured.forEach((insured, index) => {
        insured.order = `${index + 1}`;
      });

      onPageStateUpdate(newPageState);
    }
  };

  const onInsuredRowDelete = (rowIndex: number) => {
    if (!isDisabled) {
      const newPageState = cloneDeep(pageState);
      newPageState.values.policyPersons.insured = removeObjectAtIndex(
        newPageState.values.policyPersons.insured,
        rowIndex
      );
      newPageState.errors.policyPersons.insured = removeObjectAtIndex(
        newPageState.errors.policyPersons.insured,
        rowIndex
      );
      newPageState.touched.policyPersons.insured = removeObjectAtIndex(
        newPageState.touched.policyPersons.insured,
        rowIndex
      );

      newPageState.values.policyPersons.insured.forEach((insured, index) => {
        insured.order = `${index + 1}`;
      });

      setLists((prevLists) => {
        // Create new objects to store the updated values
        const newId: Record<number, Record<string, string>> = {};
        const newInsuredAddresses: Record<number, Record<string, string>> = {};

        // Iterate over the keys of the previous id object
        Object.keys(prevLists.insuredAddresses).forEach((key) => {
          const keyNum = Number(key);
          if (keyNum < rowIndex) {
            // Copy the data as-is if the index is before the one to delete
            newId[keyNum] = prevLists.id[keyNum];
            newInsuredAddresses[keyNum] = prevLists.insuredAddresses[keyNum];
          } else if (keyNum > rowIndex) {
            // Shift the indices down by 1 for the entries after the deleted index
            newId[keyNum - 1] = prevLists.id[keyNum];
            newInsuredAddresses[keyNum - 1] =
              prevLists.insuredAddresses[keyNum];
          }
        });

        return {
          id: newId,
          insuredAddresses: newInsuredAddresses,
        };
      });

      onPageStateUpdate(newPageState);
    }
  };

  if (!loaded) {
    return null;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Insured">
        {(insuredValues || []).map((v) => renderInsuredRow(v))}
        {newPersonDrawerOpen !== -1 && currentSection === 'insured' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen !== -1}
            onClose={() => setNewPersonDrawerOpen(-1)}
            section={currentSection}
            successRoute="/production/amendment/"
            proposalId={currentAmendmentID}
            lineExternalCode={generalData.line.externalCode}
            onSubmitSuccess={onPersonAddition}
          />
        )}
        {newAddressDrawerOpen !== -1 && currentSection === 'insured' && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen !== -1}
            onClose={() => setNewAddressDrawerOpen(-1)}
            personId={insuredValues?.[newAddressDrawerOpen]?.id || ''}
            onSubmitSuccess={onAddressAddition}
          />
        )}
        {insuredValues.length < maxNumber && (
          <button className={classes.addBtn} onClick={() => onInsuredRowAdd()}>
            <AddCircleOutlineIcon color="error" fontSize="small" />
          </button>
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default InsuredWidget;
